import { createReducer, on } from '@ngrx/store';
import {
  ReviewPageActions,
  StandingAuthorizationsActions,
  TransactionFormActions,
  TransactionsTableActions,
  WireActions,
} from '../../create-request/actions';
import { Templates } from '../models/WireTemplates';
import { callWireTemplatesGetListTable } from '../../create-request/actions/wire.actions';
import { ConfirmationPageActions } from '../../create-request/actions';
import { ViewPageActions } from '../../transaction-view/actions';
import { DraftCommonWire, Drafts } from '../../create-request/models/wire/WireTemplatesResponse';

export const templatesKey = 'templates';

export interface State extends Templates {}

export const initialTemplatesState: State = {
  templatesList: [],
  templateListForm: [],
  templatesCallSucceeded: false,
  createWireTemplateCallCompleted: false,
  createWireTemplateCallSuccessful: false,
  patchWireTemplateCallCompleted: false,
  patchWireTemplateCallSuccessful: false,
  templatesPrefilledFlowInProgress: false,
  templateStartTransactionFlowInProgress: false,
  templatesAddUpdateEditNickNameCallSuccessful: false,
  templatesDeleteCallFinished: false,
  templateDeleteCallSuccessful: false,
  templatesListCallTableSucceeded: false
};

export const templatesReducer = createReducer(
  initialTemplatesState,
  on(WireActions.callWireTemplatesGetList,
     WireActions.callWiresCreateTemplate,
     (_) => {
    return initialTemplatesState;
  }),
  on(WireActions.callWireTemplatesGetListTableSuccessful, WireActions.callWireTemplatesGetListSuccessful, (state, response) => {
    return {
      ...state,
      templateListForm: response.templates?.map(template => mapDraft(template)),
      templatesList: response.templates,
      templatesCallSucceeded: true  
    }
  }),
  on(WireActions.callWireTemplatesGetListFailed, WireActions.callWireTemplatesGetListTableFailed,  (state) => {
    return {
      ...state,
      templatesCallSucceeded: false
    };
  }),
  on(WireActions.callWiresCreateTemplateSuccessful, (state) => {
    return {
      ...state,
      createWireTemplateCallCompleted: true,
      createWireTemplateCallSuccessful: true
    };
  }),
  on(WireActions.callWiresCreateTemplateFailed, (state) => {
    return {
      ...state,
      createWireTemplateCallCompleted: true,
      createWireTemplateCallSuccessful: false
    };
  }),
  on(WireActions.callWiresPatchTemplateSuccessful, (state) => {
    return {
      ...state,
      patchWireTemplateCallCompleted: true,
      patchWireTemplateCallSuccessful: true
    };
  }),
  on(WireActions.callWiresPatchTemplateFailed, (state) => {
    return {
      ...state,
      patchWireTemplateCallCompleted: true,
      patchWireTemplateCallSuccessful: false
    };
  }),
  on(TransactionFormActions.templatePrefilledFlowStarted, (state) => {
    return {
      ...state, 
      templatesPrefilledFlowInProgress: true,
      templateStartTransactionFlowInProgress: false
    }
  }),
  on(TransactionFormActions.templatePrefilledFlowComplete, (state) => {
    return {
      ...state, 
      templatesPrefilledFlowInProgress: false
    }
  }),
  on(callWireTemplatesGetListTable, (state) => {
    return {
      ...state,
      templatesList: [],
      templatesDeleteCallFinished: false,
      templateDeleteCallSuccessful: false
    }
  }),
  on(TransactionsTableActions.addUpdateDeleteNicknameTemplates, TransactionsTableActions.updateDraftFailure, (state) => {
    return {
      ...state,
      templatesAddUpdateEditNickNameCallSuccessful: false
    }
  }),
  on(
    TransactionsTableActions.updateDraftSuccess,
    (state, response) => {
      return {
        ...state,
        templatesAddUpdateEditNickNameCallSuccessful: true,
        templatesList: state.templatesList.map((template) =>
          template.draftId === response.draftId ? response : template ),
        templateListForm: state.templateListForm.map(template => template.id === response.draftId ? mapDraft(response) : template)
      };
    }
  ),
  on(TransactionsTableActions.startTransactionClicked, ViewPageActions.startTransactionButtonClicked, (state) => {
    return {
      ...state,
      templateStartTransactionFlowInProgress: true
    }
  }),
  on(
    WireActions.wireInputV3TemplateFlowFailed,
    WireActions.wireInputFailedV3,
    StandingAuthorizationsActions.loadSloas,
    TransactionFormActions.transactionTypeChanged,
    TransactionFormActions.cancelClicked,
    (state) => {
      return {
        ...state,
        templateStartTransactionFlowInProgress: false
      };
    }
  ),
  on(TransactionsTableActions.deleteTemplate, (state) => {
    return {
      ...state,
      templateDeleteCallSuccessful: false,
      templatesDeleteCallFinished: false
    }
  }),
  on(TransactionsTableActions.deleteTemplateFailure, (state) => {
    return {
      ...state,
      templatesDeleteCallFinished: true,
      templateDeleteCallSuccessful: false
    }
  }),
  on(TransactionsTableActions.deleteTemplateSuccess, (state) => {
    return {
      ...state,
      templatesDeleteCallFinished: true,
      templateDeleteCallSuccessful: true
    }
  }),
  on(
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    TransactionFormActions.saveTemplateSelectionChanged,
    TransactionFormActions.cancelClicked,
    ReviewPageActions.cancelClicked,
    (state) => {
    return {
      ...state,
      createWireTemplateCallCompleted: false,
      createWireTemplateCallSuccessful: false
    }
  }),
  on(WireActions.callWireTemplatesGetListTableSuccessful, (state) => {
    return {
      ...state,
      templatesListCallTableSucceeded: true
    };
  }),
  on(TransactionsTableActions.currentTabUpdated, WireActions.callWireTemplatesGetListTableFailed, (state) => {
    return {
      ...state,
      templatesListCallTableSucceeded: false
    }
  }),
);

function mapDraft(template: DraftCommonWire): Drafts {
  return {
    id: template.draftId,
    beneficiary: template.draftObject.beneficiary,
    beneficiaryBankId: template.draftObject.beneficiaryBankId,
    nickname: template.draftObject.nickname,
    isThirdParty: template.draftObject.isThirdParty,
    isIsoCompliant: template.draftObject.isIsoCompliant,
    isIrsAba: template.draftObject.isIrsAba ?? false,
    taxRemittance: template.draftObject.taxRemittance ? {
      taxId: template.draftObject.taxRemittance.taxId ?? '',
      taxMonthCode: template.draftObject.taxRemittance.taxMonthCode ?? '',
      taxTypeCode: template.draftObject.taxRemittance.taxTypeCode ?? '',
      taxYear: template.draftObject.taxRemittance.taxYear ?? '',
      additionalInfo: template.draftObject.taxRemittance.additionalInfo ?? ''
    } : {
      taxId: '',
      taxMonthCode: '',
      taxTypeCode: '',
      taxYear: '',
      additionalInfo: ''  
    }
  }
}
